import type { ValueFormat } from '@manaro/contracts'

const MONTH_DAY = new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'long' })
const MONTH_DAY_YEAR = new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' })
const DAY_MS = 24 * 60 * 60 * 1000

export function formatDate(iso: string): string {
  const date = new Date(iso)
  const sameYear = date.getFullYear() === new Date().getFullYear()
  return (sameYear ? MONTH_DAY : MONTH_DAY_YEAR).format(date)
}

export function plural(count: number, forms: [string, string, string]): string {
  const mod100 = Math.abs(count) % 100
  const mod10 = mod100 % 10
  if (mod100 > 10 && mod100 < 20) return forms[2]
  if (mod10 === 1) return forms[0]
  if (mod10 > 1 && mod10 < 5) return forms[1]
  return forms[2]
}

export function formatRelative(iso: string): string {
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / DAY_MS)

  if (days <= 0) return 'сегодня'
  if (days === 1) return 'вчера'
  if (days < 7) return `${days} ${plural(days, ['день', 'дня', 'дней'])} назад`
  if (days < 31) {
    const weeks = Math.floor(days / 7)
    return `${weeks} ${plural(weeks, ['неделю', 'недели', 'недель'])} назад`
  }
  const months = Math.floor(days / 30)
  return `${months} ${plural(months, ['месяц', 'месяца', 'месяцев'])} назад`
}

export function formatTime(iso: string): string {
  return new Intl.DateTimeFormat('ru-RU', { hour: '2-digit', minute: '2-digit' }).format(new Date(iso))
}

export function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toLocaleUpperCase('ru') ?? '')
    .join('')
}

const NUMBER = new Intl.NumberFormat('ru-RU')

const currencyFormatters = new Map<string, Intl.NumberFormat>()

function currencyFormatter(currency: string): Intl.NumberFormat {
  const cached = currencyFormatters.get(currency)
  if (cached) return cached

  const formatter = new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency,
    currencyDisplay: 'narrowSymbol',
    maximumFractionDigits: 0,
  })
  currencyFormatters.set(currency, formatter)
  return formatter
}

export function formatValue(value: number | null, format: ValueFormat, currency = 'UAH'): string {
  if (value === null || Number.isNaN(value)) return '—'

  switch (format) {
    case 'currency':
      return currencyFormatter(currency).format(value)
    case 'percent':
      return `${NUMBER.format(Number(value.toFixed(1)))}%`
    case 'points':
      return `${NUMBER.format(Number(value.toFixed(1)))} п.п.`
    default:
      return NUMBER.format(Math.round(value))
  }
}

export function formatDelta(delta: { value: number; format: ValueFormat }, currency = 'UAH'): string {
  const sign = delta.value > 0 ? '+' : delta.value < 0 ? '−' : ''
  return `${sign}${formatValue(Math.abs(delta.value), delta.format, currency)}`
}

export function formatCompact(value: number): string {
  if (Math.abs(value) < 10_000) return NUMBER.format(value)
  return `${NUMBER.format(Number((value / 1000).toFixed(1)))} тыс.`
}
